﻿import { useRef, useState, useEffect } from 'react'
import { log } from '@/utils/logger'
import { ScanText, Image as ImageIcon, Camera, Loader2, Sparkles } from 'lucide-react'
import { useValuationStore } from '@/store/valuationStore'
import { useUiStore } from '@/store/uiStore'
import { useAuthStore } from '@/store/authStore'
import { cn } from '@/utils/cn'
import { CameraModal } from '@/components/ui/CameraModal'
import { PhotoLightbox } from '@/components/ui/PhotoLightbox'
import { processAiImages, detectAiImages, aggregateAiImages, type CodedError } from '@/services/claimService'
import { processOcrImage, processOcrSection, normaliseOcrValue } from '@/services/ocrService'
import { decodeVin } from '@/services/vinService'
import { FIELD_VALIDATORS } from '@/utils/fieldValidators'
// import type { FormValues } from '@/types'
import { AiProcessModal, AiProcessField } from '@/components/ui/AiProcessModel'
import { mapResponseToForm, matchSelectValue } from '@/utils/fieldResponseMapper'
import { FIELD_RESPONSE_MAPPING } from '@/data/fieldResponseMapping'
import { fetchAttributeDefinitions } from '@/services/attributeService'
import { useRefStore } from '@/store/refStore'
import { REF_KEYS } from '@/data/refKeys'
import { ALL_SECTIONS, FIELD_LABEL_MAP, FIELD_TYPE_MAP } from '@/data/allSections'
import type { FieldResponsePayload, MappedField } from '@/types'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { getMessage, type MessagePaths } from '@/utils/messages'
import { getPhotoLimit } from '@/config/photoLimits'
import { MAX_IMAGE_SIZE_MB, isFileSizeWithinLimit } from '@/config/imageSize'
import { computeFileHash } from '@/utils/fileHash'
import { useImageProcessor } from '@/hooks/useImageProcessor'
import { defaultImageProcessingConfig } from '@/config/imageProcessing'
import { ProcessingError } from '@/types/imageProcessing'
import { useAttributeStore } from '@/store/attributeStore'
import { mapAttributesToFields } from '@/utils/attributeMapper'
import { normaliseToKey } from '@/data/refCategories'

const INFO_LABEL_ALLOWED_EXT = new Set([
  '.jpg', '.jpeg', '.jpe', '.png'
])

const AI_UPLOAD_ALLOWED_EXT = new Set([
  ...INFO_LABEL_ALLOWED_EXT
])

// File-picker `accept` filter — keeps the OS dialog restricted to the same
// extensions enforced post-selection by AI_UPLOAD_ALLOWED_EXT / INFO_LABEL_ALLOWED_EXT.
const IMAGE_ACCEPT = [...AI_UPLOAD_ALLOWED_EXT].join(',')

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

type QueueItemKind = 'ai' | 'ocr'
// batchId is set only when 2+ AI images were queued from the same handleFileChange call —
// used to aggregate results into one popup instead of one popup per image.
interface QueueItem { file: File; photoId: string; kind: QueueItemKind; batchId?: string }
interface BatchAccumulator { fields: Map<string, MappedField>; mismatchCount: number; unsupportedCount: number; duplicateCount: number; failedCount: number; photoIds: string[] }
type HashCheckResult = { status: 'ok'; hash: string } | { status: 'duplicate' }

interface PhotoStripProps {
  sectionId: string
  hasVisibleFields?: boolean
}

export function PhotoStrip({ sectionId, hasVisibleFields = false }: PhotoStripProps) {
  const { photos, addPhoto, removePhoto, removePhotoLocal, applyAIFields, setAiResult, setAiRawPayload, clearSectionAIMetadata, loadPhotosFromServer, updatePhotoServerNames, clearSectionOcrMetadata, setOcrResult, photoResults, setPhotoResult, clearPhotoResult, clearSpecificFields, promoteFieldConfidence } = useValuationStore()
  const caseId = useValuationStore((s) => s.caseId ?? '')
  const userId = String(useAuthStore.getState().user?.userId ?? '')
  const isSubmitted = useValuationStore((s) => s.status === 'submitted')
  const { showToast, vinDecoding, setVinDecoding } = useUiStore()

  const requireClaimRef = (): boolean => {
    if (!userId || !caseId) {
      showToast(getMessage('photo.error.missing_claim_ref'), 'error')
      return false
    }
    return true
  }

  const checkPhotoLimit = (fileCount: number = 0, mode: 'toast' | 'popup' = 'toast', onRetry?: () => void): boolean => {
    const limit = getPhotoLimit(sectionId)
    const current = useValuationStore.getState().photos.filter((p) => p.sectionId === sectionId).length
    const totalAfterUpload = current + fileCount
    const remaining = Math.max(0, limit - current)

    if (totalAfterUpload > limit) {
      if (mode === 'popup') {
        // Show popup instead of toast
        setLimitExceededCounts({ remaining, selected: fileCount, retryCallback: onRetry })
        setLimitExceededOpen(true)
        // Hide loading spinners when showing limit popup
        setLoading(false)
        setOcrLoading(false)
        return false
      } else {
        // Original behavior: toast only when at/over limit with no files selected
        if (fileCount === 0) {
          showToast(getMessage('photo_strip.error_limit_reached', { limit: String(limit) }), 'error')
        }
        return false
      }
    }
    return true
  }

  // Flat size-limit check retained for the OCR/Info-Label path, which is
  // out of scope for the compression pipeline below.
  const checkFileSize = (sizeInBytes: number): boolean => {
    if (!isFileSizeWithinLimit(sizeInBytes)) {
      showToast(getMessage('photo_strip.error_size_exceeded', { limit: String(MAX_IMAGE_SIZE_MB) }), 'error')
      return false
    }
    return true
  }

  const { processImage } = useImageProcessor()

  /**
   * AI-inference upload path only: validates, and (when >5MB) compresses/
   * resizes the file off the main thread. Returns the processed File to use
   * everywhere downstream (upload, thumbnail, preview) — the original is
   * discarded. Returns null and shows a toast if the file is invalid or
   * cannot be brought within the configured limits. Does not apply to the
   * OCR/Info-Label path.
   */
  const processSelectedFile = async (file: File, key: string): Promise<File | null> => {
    try {
      const result = await processImage(file, key)
      return result.file
    } catch (err) {
      if (err instanceof ProcessingError) {
        const limit = String(defaultImageProcessingConfig.maxSizeMB)
        const maxSizeMB = String(defaultImageProcessingConfig.maxOriginalSizeMB ?? 20)
        const messageKey =
          err.reason === 'invalid-type'
            ? 'photo_strip.error_invalid_type'
            : err.reason === 'corrupt'
              ? 'photo_strip.error_corrupt'
              : err.reason === 'size-exceeds-absolute-max'
                ? 'photo_strip.error_exceeds_max_size'
                : err.reason === 'dimensions-exceed-max'
                  ? 'photo_strip.error_dimensions_exceed_max'
                  : err.reason === 'compression-timeout'
                    ? 'photo_strip.error_compression_timeout'
                    : err.reason === 'aborted'
                      ? null
                      : err.reason === 'dimension-floor-hit'
                        ? 'photo_strip.error_dimension_floor'
                        : 'photo_strip.error_compression_failed'
        const messageParams: Record<string, string> =
          err.reason === 'size-exceeds-absolute-max' ? { maxSizeMB } : { limit }
        if (messageKey) {
          showToast(getMessage(messageKey, messageParams), 'error')
        }
      } else {
        log.error('Image processing failed', err instanceof Error ? err : new Error(String(err)))
        showToast(getMessage('photo_strip.error_compression_failed', { limit: String(defaultImageProcessingConfig.maxSizeMB) }), 'error')
      }
      return null
    }
  }

  /**
   * Computes the SHA-256 hash of blob and checks it against every photo
   * already in this section (regardless of filename). Pure detector — does
   * not toast or otherwise report the result; callers decide how to surface
   * a duplicate (toast for single-image flows, aggregate count for batches).
   */
  const checkContentHash = async (blob: Blob): Promise<HashCheckResult> => {
    const hash = await computeFileHash(blob)
    const sectionPhotos = useValuationStore.getState().photos.filter((p) => p.sectionId === sectionId)
    if (sectionPhotos.some((p) => p.contentHash === hash)) {
      return { status: 'duplicate' }
    }
    return { status: 'ok', hash }
  }

  const fileInputRef = useRef<HTMLInputElement>(null)
  const processingRef = useRef(false)
  const queueRef = useRef<QueueItem[]>([])
  const pendingOcrPhotoIdRef = useRef<string | undefined>(undefined)
  // Multi-image AI batch aggregation (2+ files selected together in one handleFileChange
  // call): tracks how many queued items remain per batchId, and accumulates successful
  // fields (highest-confidence-wins per key) plus a rejected-mismatch count, flushed into
  // one AiProcessModal popup when the batch's last item finishes. Single-image uploads/
  // captures never set a batchId and keep the original per-item popup behavior untouched.
  const batchRemainingRef = useRef<Map<string, number>>(new Map())
  const batchAccumulatorRef = useRef<Map<string, BatchAccumulator>>(new Map())

  const [loading, setLoading] = useState(false)
  const [aiProcessingCurrent, setAiProcessingCurrent] = useState(0)
  const [aiProcessingTotal, setAiProcessingTotal] = useState(0)
  const [cameraOpen, setCameraOpen] = useState(false)
  const [limitExceededOpen, setLimitExceededOpen] = useState(false)
  const [limitExceededCounts, setLimitExceededCounts] = useState({ remaining: 0, selected: 0, retryCallback: undefined as (() => void) | undefined })

  // const [aiFields, setAiFields] = useState<AiProcessField[]>([])

  const [pendingMappedFields, setPendingMappedFields] = useState<MappedField[]>([])
  // Photo ids to delete (from local + blob) if the user clicks Cancel on the AI-results
  // popup — per docs/photo_enhancements.md Â§11, Cancel on a *new upload* removes it;
  // Cancel on re-detect/aggregate-view (existing, already-committed photos) must not
  // delete anything, so those paths set this to []. A batch popup holds every
  // successfully-processed photo id from that batch (mismatched ones are already removed
  // at detection time, independent of Cancel).
  const [pendingPhotoIds, setPendingPhotoIds] = useState<string[]>([])
  const [aiModalOpen, setAiModalOpen] = useState(false)
  // Set only when a multi-image AI batch's flushed popup includes a rejected-mismatch
  // count banner (see batchRemainingRef/batchAccumulatorRef above). Not used by the
  // single-image path, which keeps the standalone bodyMismatchInfo/ConfirmDialog.
  const [pendingWarningText, setPendingWarningText] = useState<string | null>(null)
  const [bodyMismatchInfo, setBodyMismatchInfo] = useState<{ currentValue: string; responseValue: string } | null>(null)
  const [vtMismatchInfo, setVtMismatchInfo] = useState<{ currentValue: string; responseValue: string } | null>(null)
  const [viewImageDataPhotoId, setViewImageDataPhotoId] = useState<string | null>(null)
  // const [viewAggOcrOpen, setViewAggOcrOpen] = useState(false)
  //const aggregatedOcrFields = ocrResults[sectionId] ?? []

  const [removeModalOpen, setRemoveModalOpen] = useState(false)
  const [removeModalPhotoId, setRemoveModalPhotoId] = useState<string | null>(null)
  const [removeModalFields, setRemoveModalFields] = useState<AiProcessField[]>([])
  // Retains the original MappedField[] (with fieldKey) so clearSpecificFields uses the
  // correct field key and never touches vehicleType / use / type values.
  const [removeModalMappedFields, setRemoveModalMappedFields] = useState<MappedField[]>([])
  // True when one or more fields were excluded from the popup because they are still
  // backed by an identical value in another uploaded image.
  const [removeModalHasRetainedFields, setRemoveModalHasRetainedFields] = useState(false)
  // Confidence promotions to apply on confirm: backed fields whose confidence badge
  // should be updated to the highest score from the remaining images.
  const [removeModalConfidenceUpdates, setRemoveModalConfidenceUpdates] = useState<Array<{ sectionId: string; fieldKey: string; confidence: number }>>([])

  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)
  const [ocrLoading, setOcrLoading] = useState(false)
  const [ocrMappedFields, setOcrMappedFields] = useState<MappedField[]>([])
  const [ocrFields, setOcrFields] = useState<AiProcessField[]>([])
  const [ocrModalOpen, setOcrModalOpen] = useState(false)
  const [vinMappedFields, setVinMappedFields] = useState<MappedField[]>([])
  const [vinModalOpen, setVinModalOpen] = useState(false)
  const [vinDecodeConfirmOpen, setVinDecodeConfirmOpen] = useState(false)
  const [vinDecodeSnapshot, setVinDecodeSnapshot] = useState<MappedField[]>([])
  const [unsupportedExt, setUnsupportedExt] = useState<{ ext: string; source: 'image' | 'info-label' } | null>(null)
  const [uploadMenuOpen, setUploadMenuOpen] = useState(false)
  const [additionalUploadMenuOpen, setAdditionalUploadMenuOpen] = useState(false)
  const [bodyUploadMenuOpen, setBodyUploadMenuOpen] = useState(false)
  const [captureMenuOpen, setCaptureMenuOpen] = useState(false)
  const [additionalCaptureMenuOpen, setAdditionalCaptureMenuOpen] = useState(false)
  const [bodyCaptureMenuOpen, setBodyCaptureMenuOpen] = useState(false)
  const [captureKind, setCaptureKind] = useState<'ai' | 'ocr'>('ai')
  const uploadMenuRef = useRef<HTMLDivElement>(null)
  const infoLabelInputRef = useRef<HTMLInputElement>(null)
  const imageInputRef = useRef<HTMLInputElement>(null)
  const additionalUploadMenuRef = useRef<HTMLDivElement>(null)
  const additionalInfoLabelInputRef = useRef<HTMLInputElement>(null)
  const bodyUploadMenuRef = useRef<HTMLDivElement>(null)
  const bodyInfoLabelInputRef = useRef<HTMLInputElement>(null)
  const captureMenuRef = useRef<HTMLDivElement>(null)
  const additionalCaptureMenuRef = useRef<HTMLDivElement>(null)
  const bodyCaptureMenuRef = useRef<HTMLDivElement>(null)


  // Load images from Blob Storage on mount (and whenever caseId becomes available).
  // Must specify both blobSections since images live under ai/{sectionId} and ocr/{sectionId}.
  useEffect(() => {
    if (userId && caseId) {
      loadPhotosFromServer(userId, caseId, sectionId, 'ai')
      loadPhotosFromServer(userId, caseId, sectionId, 'ocr')
    }
  }, [sectionId, userId, caseId, loadPhotosFromServer])

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!uploadMenuOpen) return
    const handleClickOutside = (e: MouseEvent) => {
      if (uploadMenuRef.current && !uploadMenuRef.current.contains(e.target as Node)) {
        setUploadMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [uploadMenuOpen])

  useEffect(() => {
    if (!additionalUploadMenuOpen) return
    const handler = (e: MouseEvent) => {
      if (!additionalUploadMenuRef.current?.contains(e.target as Node)) {
        setAdditionalUploadMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [additionalUploadMenuOpen])

  useEffect(() => {
    if (!bodyUploadMenuOpen) return
    const handler = (e: MouseEvent) => {
      if (!bodyUploadMenuRef.current?.contains(e.target as Node)) {
        setBodyUploadMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [bodyUploadMenuOpen])

  useEffect(() => {
    if (!captureMenuOpen) return
    const handler = (e: MouseEvent) => {
      if (!captureMenuRef.current?.contains(e.target as Node)) {
        setCaptureMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [captureMenuOpen])

  useEffect(() => {
    if (!additionalCaptureMenuOpen) return
    const handler = (e: MouseEvent) => {
      if (!additionalCaptureMenuRef.current?.contains(e.target as Node)) {
        setAdditionalCaptureMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [additionalCaptureMenuOpen])

  useEffect(() => {
    if (!bodyCaptureMenuOpen) return
    const handler = (e: MouseEvent) => {
      if (!bodyCaptureMenuRef.current?.contains(e.target as Node)) {
        setBodyCaptureMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [bodyCaptureMenuOpen])

  const sectionPhotos = photos.filter((p) => p.sectionId === sectionId)
  
  const isoToDisplay = (v: string) => {
    const m = v?.match(/^(\d{4})-(\d{2})-(\d{2})$/)
    return m ? `${m[2]}/${m[3]}/${m[1]}` : v
  }

  const toModalFields = (fields: MappedField[]) =>
    fields.map((mf) => ({
      label: mf.label || FIELD_LABEL_MAP[mf.fieldKey] || mf.fieldKey,
      aiValue: isoToDisplay(mf.value),
      value: isoToDisplay(useValuationStore.getState().formValues[mf.sectionId]?.[mf.fieldKey] || 'NA'),
      source: mf.source,
      confidence: mf.confidence,
      sectionId: mf.sectionId,
    }))

  // ── Shared: parse OCR response → mappedFields → open OCR modal ──────────
  const applyOcrResponse = (response: Awaited<ReturnType<typeof processOcrImage>>, photoId: string) => {
    const ocrSections = response?.data?.sections ?? []
    const payload: FieldResponsePayload = {
      sections: Object.fromEntries(
        ocrSections.map(({ section, fields }) => [
          section,
          {
            section,
            fields: Object.fromEntries(
              Object.entries(fields).map(([key, entry]) => [
                key,
                { value: normaliseOcrValue(String(entry.value ?? '')), confidence: entry.confidence ?? 1, source: entry.source },
              ])
            ),
          },
        ])
      ),
    }
    // Build a reverse lookup: fieldKey → backend section slug (e.g. "body-information")
    // Used to reject fields that the frontend resolved to a different section than the backend mapped them to.
    const backendFieldSection = new Map<string, string>()
    for (const { section, fields } of ocrSections) {
      for (const key of Object.keys(fields)) {
        backendFieldSection.set(key, section)
      }
    }

    const { mappedFields: allMappedFields } = mapResponseToForm(payload, buildSectionsForMapping())
    const mappedFields = allMappedFields
      .map((f) => ({ ...f, source: getMessage('field_input.source_info_label') }))
      .map((f) => f.fieldKey === 'vin' ? { ...f, value: f.value.replace(/\s+/g, '').toUpperCase() } : f)
      .filter((f) => {
        const backendSection = backendFieldSection.get(f.fieldKey)
        // Reject when the backend placed this field in a different section than the form resolved it to
        if (backendSection && backendSection !== f.sectionId) return false
        // Confidence gating already happened server-side (ocr.mapper.ts — per-field
        // validation.minConfidence), so this list only ever contains fields that already
        // cleared the appropriate threshold. No re-check here.
        return (
          !(FIELD_TYPE_MAP[f.fieldKey] === 'date' && !ISO_DATE.test(f.value)) &&
          !(f.fieldKey === 'vin' && !!FIELD_VALIDATORS.vin(f.value))
        )
      })
    // Vehicle Type mismatch guard — same rule as FormRecognizer
    const vtDetected = mappedFields.find(f => f.fieldKey === 'vehicleType')
    const vtFormCurrent = (useValuationStore.getState().formValues['basic-information'] as Record<string, string>)?.['vehicleType'] ?? ''
    if (
      vtDetected?.value &&
      vtFormCurrent &&
      vtFormCurrent !== 'Select' &&
      vtDetected.value.toLowerCase() !== vtFormCurrent.toLowerCase()
    ) {
      const pendingId = pendingOcrPhotoIdRef.current
      if (pendingId) {
        removePhoto(pendingId, { forceDelete: true })
        removePhotoLocal(pendingId)
        pendingOcrPhotoIdRef.current = undefined
      }
      setVtMismatchInfo({ currentValue: vtFormCurrent, responseValue: vtDetected.value })
      return
    }

    if (mappedFields.length === 0) {
      setOcrFields([])
      setOcrMappedFields([])
    } else {
      setOcrResult(sectionId, mappedFields)
      if (photoId) setPhotoResult(photoId, mappedFields)
      setOcrMappedFields(mappedFields)
      setOcrFields(toModalFields(mappedFields))
    }
    setOcrModalOpen(true)
  }

  // ── Re-detect AI for an existing AI photo from blob storage ────────────
  const handleRedetect = async (photo: typeof sectionPhotos[0]) => {
    if (!photo.serverName) return
    const caseId = useValuationStore.getState().caseId ?? ''
    const userId = String(useAuthStore.getState().user?.userId ?? '')
    if (!userId || !caseId) { showToast(getMessage('photo.error.missing_claim_ref'), 'error'); return }
    setLoading(true)
    try {
      const response = await detectAiImages(userId, caseId, `ai/${sectionId}`, photo.serverName)
      const results = response?.data?.results ?? []
      const mergedFields = results.reduce((acc, r) => {
        for (const [key, entry] of Object.entries(r.fields ?? {})) {
          if (!acc[key] || (entry?.confidence ?? 0) > (acc[key]?.confidence ?? 0)) {
            acc[key] = { value: String(entry.value ?? ''), confidence: entry.confidence, source: entry.source }
          }
        }
        return acc
      }, {} as Record<string, { value: string; confidence: number; source: string }>)
      const aiPayload: FieldResponsePayload = {
        sections: { [sectionId]: { section: sectionId, fields: mergedFields } },
      }
      const redetectRaw = extractUseTypeFromPayload(aiPayload)
      const redetectUseType = (redetectRaw.use !== undefined || redetectRaw.type !== undefined)
        ? await ensureBodyAttrsForAiPayload(redetectRaw)
        : undefined
      const redetectMismatch = redetectUseType ? detectBodyTypeMismatch(redetectUseType) : null
      if (redetectMismatch) { setBodyMismatchInfo(redetectMismatch); return }
      const { mappedFields } = mapResponseToForm(aiPayload, buildSectionsForMapping(redetectUseType))
      if (mappedFields.length === 0) {
        showToast(getMessage('ai.error.process_failed', { filename: photo.name ?? '' }), 'error')
        return
      }
      setPendingMappedFields(mappedFields)
      setPendingPhotoIds([])
      setAiModalOpen(true)
    } catch (err) {
      log.error('Re-detect failed', err instanceof Error ? err : new Error(String(err)))
      showToast(getMessage('ai.error.process_failed', { filename: photo.name ?? '' }), 'error')
    } finally {
      setLoading(false)
    }
  }

  // ── Re-scan an existing OCR photo from blob storage ─────────────────────
  const handleRescan = async (photo: typeof sectionPhotos[0]) => {
    if (!photo.serverName) return
    const caseId = useValuationStore.getState().caseId ?? ''
    const userId = String(useAuthStore.getState().user?.userId ?? '')
    setOcrLoading(true)
    try {
      const blobPath = `${userId}/${caseId}/ocr/${sectionId}/${photo.serverName}`
      const response = await processOcrImage(null, userId, caseId, sectionId, blobPath)
      applyOcrResponse(response, photo.id)
    } catch (err) {
      log.error('Rescan failed', err instanceof Error ? err : new Error(String(err)))
      showToast(getMessage('ocr.error.scan_failed', { filename: photo.name ?? '' }), 'error')
    } finally {
      setOcrLoading(false)
    }
  }

  // ── Process all OCR images in the section from blob storage ─────────────
  const handleViewInfoLabel = async () => {
    const caseId = useValuationStore.getState().caseId ?? ''
    const userId = String(useAuthStore.getState().user?.userId ?? '')
    if (!userId || !caseId) { showToast(getMessage('photo.error.missing_claim_ref'), 'error'); return }
    setOcrLoading(true)
    try {
      const response = await processOcrSection(userId, caseId, sectionId)
      applyOcrResponse(response, '')
    } catch (err) {
      log.error('View Info Label failed', err instanceof Error ? err : new Error(String(err)))
      showToast(getMessage('ocr.error.scan_failed', { filename: sectionId }), 'error')
    } finally {
      setOcrLoading(false)
    }
  }

  // Compares the AI-resolved use/type against the current form value.
  // Uses the resolved values from ensureBodyAttrsForAiPayload (already matched against
  // ref options) rather than pendingMappedFields, because use/type fields always have
  // options: ['Select'] in ALL_SECTIONS which would mark them pendingMatch:true.
  const detectBodyTypeMismatch = (resolvedUseType: { use?: string; type?: string }): { currentValue: string; responseValue: string } | null => {
    const fv = useValuationStore.getState().formValues
    for (const fieldKey of ['use', 'type'] as const) {
      const responseValue = resolvedUseType[fieldKey]
      if (!responseValue) continue
      const current = fv['standard-equipment']?.[fieldKey] ?? ''
      if (current && current !== 'Select' && current !== responseValue) {
        return { currentValue: current, responseValue }
      }
    }
    return null
  }

  const detectServerVehicleTypeBlock = (
    payload: FieldResponsePayload
  ): { currentValue: string; responseValue: string; blockedImageNames: string[] } | null => {
    for (const sectionData of Object.values(payload.sections ?? {})) {
      if (sectionData.vehicle_type_update_blocked) {
        const responseValue =
          sectionData.fields?.['vehicle_type']?.value ??
          sectionData.fields?.['use']?.value ??
          ''
        return {
          currentValue: sectionData.blocked_vehicle_type ?? '',
          responseValue: String(responseValue),
          blockedImageNames: sectionData.blocked_image_names ?? [],
        }
      }
    }
    return null
  }

  // Matches a raw AI use/type value against loaded ref options and ensures the
  // corresponding body attribute definitions are in attrCache.
  // Returns the matched (form-option) values to use as the buildSectionsForMapping override.
  // Must be awaited before buildSectionsForMapping so attrCache is populated.
  const ensureBodyAttrsForAiPayload = async (
    rawUseType: { use?: string; type?: string }
  ): Promise<{ use?: string; type?: string }> => {
    const vehicleType = useValuationStore.getState().formValues['basic-information']?.['vehicleType'] ?? ''
    const attrStore = useAttributeStore.getState()
    const refLabels = useRefStore.getState().getLabels

    const resolved: { use?: string; type?: string } = {}

    if (vehicleType === 'Truck/Tractor' && rawUseType.use) {
      // Match raw AI value against loaded Use options to get the exact form option string
      const options = refLabels(REF_KEYS.TRUCK_USE) ?? []
      const matched = options.length ? (matchSelectValue(rawUseType.use, options) ?? rawUseType.use) : rawUseType.use
      const cacheKey = `TRUCK_BODY_${normaliseToKey(matched)}`
      if (!attrStore.cache[cacheKey]) {
        const attrs = await fetchAttributeDefinitions(cacheKey)
        if (attrs.length) attrStore.setCache(cacheKey, attrs)
      }
      resolved.use = matched
    } else if (vehicleType === 'Trailer' && rawUseType.type) {
      const options = refLabels(REF_KEYS.TRAILER_TYPE) ?? []
      const matched = options.length ? (matchSelectValue(rawUseType.type, options) ?? rawUseType.type) : rawUseType.type
      const cacheKey = `TRAILER_BODY_${normaliseToKey(matched)}`
      if (!attrStore.cache[cacheKey]) {
        const attrs = await fetchAttributeDefinitions(cacheKey)
        if (attrs.length) attrStore.setCache(cacheKey, attrs)
      }
      resolved.type = matched
    }

    return resolved
  }

  // Scans a raw AI payload for 'use' or 'type' field keys (resolved through
  // FIELD_RESPONSE_MAPPING aliases, case-insensitive) and returns their raw values.
  // Used to gate body-information field mapping on AI image upload.
  const extractUseTypeFromPayload = (payload: FieldResponsePayload): { use?: string; type?: string } => {
    const result: { use?: string; type?: string } = {}
    for (const sectionData of Object.values(payload.sections ?? {})) {
      for (const [rawKey, fieldData] of Object.entries(sectionData.fields ?? {})) {
        if (fieldData.value == null) continue
        const lk = rawKey.toLowerCase()
        const resolved = Object.entries(FIELD_RESPONSE_MAPPING).find(([k]) => k.toLowerCase() === lk)?.[1] ?? lk
        if (resolved === 'use')  result.use  = String(fieldData.value)
        if (resolved === 'type') result.type = String(fieldData.value)
      }
    }
    return result
  }

  // Builds the section list passed to mapResponseToForm.
  // Body-information fields are included only when:
  //   - Called from an OCR path (no payloadOverride) and use/type is already in formValues, OR
  //   - Called from an AI path with a payloadOverride that carries the use/type the AI detected.
  // Passing undefined keeps the existing OCR/aggregate behaviour unchanged.
  // Also injects live ref-store options into the use/type fields so mapResponseToForm
  // can match AI values against real dropdown options (not just the sentinel ['Select']).
  const buildSectionsForMapping = (payloadOverride?: { use?: string; type?: string }) => {
    const { formValues: fv } = useValuationStore.getState()
    const vehicleType = fv['basic-information']?.['vehicleType'] ?? ''
    const useValue  = payloadOverride?.use  ?? fv['standard-equipment']?.['use']  ?? ''
    const typeValue = payloadOverride?.type ?? fv['standard-equipment']?.['type'] ?? ''
    const attrCache = useAttributeStore.getState().cache
    const refLabels = useRefStore.getState().getLabels

    let bodyFields: ReturnType<typeof mapAttributesToFields> = []
    if (vehicleType === 'Truck/Tractor' && useValue && useValue !== 'Select') {
      const attrs = attrCache[`TRUCK_BODY_${normaliseToKey(useValue)}`]
      if (attrs?.length) bodyFields = mapAttributesToFields(attrs)
    } else if (vehicleType === 'Trailer' && typeValue && typeValue !== 'Select') {
      const attrs = attrCache[`TRAILER_BODY_${normaliseToKey(typeValue)}`]
      if (attrs?.length) bodyFields = mapAttributesToFields(attrs)
    }

    const truckUseOptions = refLabels(REF_KEYS.TRUCK_USE) ?? []
    const truckTypeOptions = refLabels(REF_KEYS.TRUCK_TYPE) ?? []
    const trailerTypeOptions = refLabels(REF_KEYS.TRAILER_TYPE) ?? []

    return ALL_SECTIONS.map((s) => {
      if (s.id === 'body-information') return { ...s, fields: bodyFields }
      if (s.id === 'standard-equipment') {
        return {
          ...s,
          fields: s.fields.map((f) => {
            if (f.id === 'use' && truckUseOptions.length > 0) return { ...f, options: truckUseOptions }
            if (f.id === 'truck_type' && truckTypeOptions.length > 0) return { ...f, options: truckTypeOptions }
            if (f.id === 'type' && trailerTypeOptions.length > 0) return { ...f, options: trailerTypeOptions }
            return f
          }),
        }
      }
      return s
    })
  }

  const handleViewAggAi = async () => {
    if (!requireClaimRef()) return
    setLoading(true)
    try {
      const response = await aggregateAiImages(userId, caseId, sectionId)
      const aiPayload = (response?.data ?? {}) as FieldResponsePayload
      const aggRaw = extractUseTypeFromPayload(aiPayload)
      const aggUseType = (aggRaw.use !== undefined || aggRaw.type !== undefined)
        ? await ensureBodyAttrsForAiPayload(aggRaw)
        : undefined
      const aggMismatch = aggUseType ? detectBodyTypeMismatch(aggUseType) : null
      if (aggMismatch) { setBodyMismatchInfo(aggMismatch); return }
      const { mappedFields } = mapResponseToForm(aiPayload, buildSectionsForMapping(aggUseType))
      if (mappedFields.length === 0) {
        showToast(getMessage('ocr.error.scan_failed', { filename: sectionId }), 'error')
        return
      }
      setPendingMappedFields(mappedFields)
      setPendingPhotoIds([])
      setAiModalOpen(true)
    } catch (err) {
      log.error('Aggregate AI failed', err instanceof Error ? err : new Error(String(err)))
      showToast(getMessage('ocr.error.scan_failed', { filename: sectionId }), 'error')
    } finally {
      setLoading(false)
    }
  }

  // Merges one image's mapped fields into a multi-image batch's accumulator, keeping the
  // higher-confidence value when the same fieldKey appears across images in the batch —
  // the same "highest confidence wins" rule handleRedetect already uses for its own merge
  // (see the mergedFields reduce above) — instead of letting the last-processed image's
  // value silently win regardless of confidence, which is what happens today.
  const mergeIntoBatchAccumulator = (batchId: string, photoId: string, mappedFields: MappedField[]) => {
    const acc = batchAccumulatorRef.current.get(batchId)
    if (!acc) return
    acc.photoIds.push(photoId)
    for (const mf of mappedFields) {
      const existing = acc.fields.get(mf.fieldKey)
      if (!existing || (mf.confidence ?? 0) > (existing.confidence ?? 0)) {
        acc.fields.set(mf.fieldKey, mf)
      }
    }
  }

  // Decrements the batch's remaining-items counter and, once every item in the batch has
  // been processed, flushes the accumulated results into one AiProcessModal popup —
  // showing the merged (highest-confidence-per-field) successful fields, if any, plus a
  // rejected-mismatch count banner if any images in the batch were rejected for a
  // body/vehicle-type mismatch.
  const finishBatchItem = (batchId: string) => {
    const remaining = (batchRemainingRef.current.get(batchId) ?? 1) - 1
    if (remaining > 0) {
      batchRemainingRef.current.set(batchId, remaining)
      return
    }
    batchRemainingRef.current.delete(batchId)
    const acc = batchAccumulatorRef.current.get(batchId)
    batchAccumulatorRef.current.delete(batchId)
    if (!acc) return

    const finalFields = Array.from(acc.fields.values())
    setPendingMappedFields(finalFields)
    setPendingPhotoIds(acc.photoIds)

    // Build warning text combining rejection reasons (mismatch + unsupported + failed).
    // Use existing fixed keys for single/double-reason cases, build dynamic text for others.
    let warningText: string | null = null
    const reasonCount = (acc.mismatchCount > 0 ? 1 : 0) + (acc.unsupportedCount > 0 ? 1 : 0) + (acc.duplicateCount > 0 ? 1 : 0) + (acc.failedCount > 0 ? 1 : 0)

    if (reasonCount === 0) {
      warningText = null
    } else if (reasonCount === 1) {
      // Single reason: use per-reason message key
      if (acc.mismatchCount > 0) {
        warningText = getMessage('photo_strip.multi_mismatch_warning', { count: String(acc.mismatchCount) })
      } else if (acc.unsupportedCount > 0) {
        warningText = getMessage('photo_strip.multi_unsupported_warning', { count: String(acc.unsupportedCount) })
      } else if (acc.duplicateCount > 0) {
        warningText = getMessage('photo_strip.multi_duplicate_warning', { count: String(acc.duplicateCount) })
      } else if (acc.failedCount > 0) {
        warningText = getMessage('photo_strip.multi_failed_warning', { count: String(acc.failedCount) })
      }
    } else if (reasonCount === 2 && acc.mismatchCount > 0 && acc.unsupportedCount > 0 && acc.duplicateCount === 0 && acc.failedCount === 0) {
      // Exactly mismatch+unsupported: use the pre-built combined key
      warningText = getMessage('photo_strip.multi_combined_rejections_warning', {
        mismatchCount: String(acc.mismatchCount),
        unsupportedCount: String(acc.unsupportedCount),
      })
    } else {
      // Any other multi-reason combination: build dynamic text joining each active reason
      const reasons: string[] = []
      if (acc.mismatchCount > 0) reasons.push(getMessage('photo_strip.multi_mismatch_warning', { count: String(acc.mismatchCount) }))
      if (acc.unsupportedCount > 0) reasons.push(getMessage('photo_strip.multi_unsupported_warning', { count: String(acc.unsupportedCount) }))
      if (acc.duplicateCount > 0) reasons.push(getMessage('photo_strip.multi_duplicate_warning', { count: String(acc.duplicateCount) }))
      if (acc.failedCount > 0) reasons.push(getMessage('photo_strip.multi_failed_warning', { count: String(acc.failedCount) }))
      warningText = reasons.join(' ')
    }
    setPendingWarningText(warningText)
    setAiModalOpen(true)
  }

  // ── Queue Processor (handles AI and OCR uploads serially) ────────────────
  const processQueue = async () => {
    if (processingRef.current) return

    processingRef.current = true

    while (queueRef.current.length > 0) {
      const { file, photoId, kind, batchId } = queueRef.current.shift()!

      if (kind === 'ocr') {
        setOcrLoading(true)
        setLoading(false)
      } else {
        setLoading(true)
        setOcrLoading(false)
        // Increment AI progress counter for multi-image uploads
        setAiProcessingCurrent(prev => prev + 1)
      }

      try {
        if (kind === 'ocr') {
          // ── OCR / Info Labels ──────────────────────────────────────
          const currentCaseId = useValuationStore.getState().caseId ?? ''
          const currentUserId = String(useAuthStore.getState().user?.userId ?? '')
          const response = await processOcrImage(file, currentUserId, currentCaseId, sectionId)

          if (response?.success && currentUserId && currentCaseId) {
            await updatePhotoServerNames(currentUserId, currentCaseId, sectionId, 'ocr')
          }

          pendingOcrPhotoIdRef.current = photoId
          applyOcrResponse(response, photoId)
        } else {
          // ── AI Inference ───────────────────────────────────────────
          const currentCaseId = useValuationStore.getState().caseId ?? ''
          const currentUserId = String(useAuthStore.getState().user?.userId ?? '')

          const response = await processAiImages(currentUserId, currentCaseId, { [sectionId]: [file] }, false)

          if (response?.success && currentUserId && currentCaseId) {
            await updatePhotoServerNames(currentUserId, currentCaseId, sectionId, 'ai')
          }

          const aiPayload = (response?.data ?? {}) as FieldResponsePayload
          const uploadRaw = extractUseTypeFromPayload(aiPayload)
          const uploadUseType = (uploadRaw.use !== undefined || uploadRaw.type !== undefined)
            ? await ensureBodyAttrsForAiPayload(uploadRaw)
            : undefined
          const uploadMismatch = uploadUseType ? detectBodyTypeMismatch(uploadUseType) : null
          if (uploadMismatch) {
            removePhoto(photoId)
            if (batchId) {
              const acc = batchAccumulatorRef.current.get(batchId)
              if (acc) acc.mismatchCount += 1
              finishBatchItem(batchId)
            } else {
              setBodyMismatchInfo(uploadMismatch)
            }
            continue
          }
          const serverBlock = detectServerVehicleTypeBlock(aiPayload)
          if (serverBlock) {
            const allPhotos = useValuationStore.getState().photos
            for (const serverName of serverBlock.blockedImageNames) {
              const photo = allPhotos.find((p) => p.serverName === serverName)
              if (photo) await removePhoto(photo.id)
            }
            if (batchId) {
              const acc = batchAccumulatorRef.current.get(batchId)
              if (acc) acc.mismatchCount += 1
              finishBatchItem(batchId)
            } else {
              setBodyMismatchInfo({ currentValue: serverBlock.currentValue, responseValue: serverBlock.responseValue })
            }
            continue
          }
          const { mappedFields } = mapResponseToForm(aiPayload, buildSectionsForMapping(uploadUseType))

          setAiRawPayload(sectionId, aiPayload)
          setAiResult(sectionId, mappedFields)
          setPhotoResult(photoId, mappedFields)
          if (batchId) {
            mergeIntoBatchAccumulator(batchId, photoId, mappedFields)
            finishBatchItem(batchId)
          } else {
            setPendingMappedFields(mappedFields)
            setPendingPhotoIds([photoId])
            setAiModalOpen(true)
          }

          // showToast(getMessage('photo.success.processed', { filename: file.name || 'Image' }))
        }
      } catch (err) {
        log.error('Photo processing failed', err instanceof Error ? err : new Error(String(err)))
        removePhotoLocal(photoId)

        // Pick the toast message based on the error code (set by claimService.ts processAiImages)
        const errorCode = (err as CodedError)?.code
        let toastKey: keyof MessagePaths = 'photo.error.process_failed'
        if (errorCode === 'TIMEOUT') {
          toastKey = 'api.error.timeout'
        } else if (errorCode === 'NETWORK') {
          toastKey = 'api.error.network'
        } else if (errorCode === 'PAYLOAD_TOO_LARGE') {
          toastKey = 'api.error.payload_too_large'
        }
        showToast(getMessage(toastKey, { filename: file.name || 'Image' }), 'error')

        // Track failed image in multi-image batch for aggregate warning banner
        if (batchId) {
          const acc = batchAccumulatorRef.current.get(batchId)
          if (acc) acc.failedCount += 1
          finishBatchItem(batchId)
        }
      }
    }

    processingRef.current = false
    setLoading(false)
    setOcrLoading(false)
    setAiProcessingCurrent(0)
    setAiProcessingTotal(0)
  }

  // Camera capture — routes to AI or OCR based on captureKind
  const handleCaptured = async (blob: Blob) => {
    if (!checkPhotoLimit(1, 'popup', undefined)) return

    const rawFile = new File([blob], 'capture.png', { type: 'image/png' })
    if (captureKind === 'ocr') { setOcrLoading(true) } else { setLoading(true) }

    let file = rawFile
    if (captureKind !== 'ocr') {
      // Compression pipeline applies to the AI-inference capture path only.
      const processed = await processSelectedFile(rawFile, `capture-${Date.now()}`)
      if (!processed) { setLoading(false); return }
      file = processed
    } else if (!checkFileSize(file.size)) {
      setOcrLoading(false)
      return
    }

    const hashResult = await checkContentHash(file)
    if (hashResult.status === 'duplicate') {
      showToast(getMessage('photo_strip.error_duplicate'), 'error')
      setLoading(false)
      setOcrLoading(false)
      return
    }
    const contentHash = hashResult.hash

    const kind = captureKind
    const photoId = addPhoto({
      sectionId,
      blob: file,
      type: 'capture',
      name: file.name,
      mimeType: file.type || 'image/png',
      blobSection: kind === 'ocr' ? 'ocr' : 'ai',
      contentHash,
    })

    queueRef.current.push({ file, photoId, kind })
    processQueue()
  }

  const handleUpload = () => {
    if (!requireClaimRef()) return
    if (!checkPhotoLimit()) return
    fileInputRef.current?.click()
  }

  // File upload handler (AI inference path) — supports multi-file selection
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files
    if (!fileList || fileList.length === 0) return
    const selectedFiles = Array.from(fileList)
    e.target.value = ''

    setLoading(true)

    // Extension pre-filter is sync/cheap, done up front so only valid
    // candidates enter the (worker-bound, concurrency-capped) processing stage.
    // For single-image uploads, show the standalone unsupported dialog;
    // for multi-image batches, count rejections to fold into the aggregate modal.
    let unsupportedCount = 0
    const extFiltered = selectedFiles.filter((file) => {
      const ext = file.name.slice(file.name.lastIndexOf('.')).toLowerCase()
      if (!AI_UPLOAD_ALLOWED_EXT.has(ext)) {
        if (selectedFiles.length === 1) {
          setUnsupportedExt({ ext, source: 'image' })
        } else {
          unsupportedCount += 1
        }
        return false
      }
      return true
    })

    // Pre-flight check: if adding all selected files exceeds limit, show popup and return
    if (!checkPhotoLimit(extFiltered.length, 'popup', () => fileInputRef.current?.click())) {
      return
    }
    const candidates = extFiltered

    // Validate/compress every candidate concurrently — actual worker
    // concurrency is capped by useImageProcessor's shared semaphore
    // (config.maxConcurrentCompressions), so this doesn't spawn one
    // worker per file even for large batches.
    const processedResults = await Promise.all(
      candidates.map((file) => processSelectedFile(file, `${file.name}-${file.size}-${file.lastModified}`))
    )

    // Adding photos to the store must stay sequential: duplicate-hash and
    // per-section limit checks depend on photos already added earlier in
    // this same batch. Collect the items that actually get added locally
    // first — only once we know the final count do we decide whether this
    // is a single-image (unchanged, per-item popup) or multi-image (batched,
    // aggregated popup) upload.
    let duplicateCount = 0
    const queuedItems: { file: File; photoId: string }[] = []
    for (const processed of processedResults) {
      if (!processed) continue
      if (!checkPhotoLimit()) break

      const hashResult = await checkContentHash(processed)
      if (hashResult.status === 'duplicate') {
        duplicateCount += 1
        continue
      }
      const contentHash = hashResult.hash

      const photoId = addPhoto({
        sectionId,
        blob: processed,
        type: 'upload',
        name: processed.name,
        mimeType: processed.type,
        blobSection: 'ai',
        contentHash,
      })

      queuedItems.push({ file: processed, photoId })
    }

    // For multi-image uploads (2+ files selected together), use batch aggregation.
    if (selectedFiles.length > 1) {
      const batchId = `batch-${Date.now()}`
      batchRemainingRef.current.set(batchId, queuedItems.length)
      batchAccumulatorRef.current.set(batchId, { fields: new Map(), mismatchCount: 0, unsupportedCount, duplicateCount, failedCount: 0, photoIds: [] })

      // Initialize progress tracking for multi-image batch
      setAiProcessingTotal(queuedItems.length)
      setAiProcessingCurrent(0)

      // Edge case: all files in the batch were rejected up front (unsupported and/or
      // duplicate, zero valid files). Flush the aggregate modal directly here with just
      // the rejection banner(s), no queue processing.
      if (queuedItems.length === 0 && (unsupportedCount > 0 || duplicateCount > 0)) {
        const reasons: string[] = []
        if (unsupportedCount > 0) reasons.push(getMessage('photo_strip.multi_unsupported_warning', { count: String(unsupportedCount) }))
        if (duplicateCount > 0) reasons.push(getMessage('photo_strip.multi_duplicate_warning', { count: String(duplicateCount) }))
        setPendingMappedFields([])
        setPendingPhotoIds([])
        setPendingWarningText(reasons.join(' '))
        setAiModalOpen(true)
        setLoading(false)
        return
      }

      for (const { file, photoId } of queuedItems) {
        queueRef.current.push({ file, photoId, kind: 'ai', batchId })
      }
    } else {
      // Single-image AI upload
      if (duplicateCount > 0) {
        showToast(getMessage('photo_strip.error_duplicate'), 'error')
      }
      setAiProcessingTotal(queuedItems.length)
      setAiProcessingCurrent(0)
      for (const { file, photoId } of queuedItems) {
        queueRef.current.push({ file, photoId, kind: 'ai' })
      }
    }

    processQueue()
  }

  const handleVinDecode = async (appliedFields: MappedField[]) => {
    const vinField = appliedFields.find(f => f.fieldKey === 'vin')
    if (!vinField?.value) return

    const vinError = FIELD_VALIDATORS.vin(vinField.value)
    if (vinError) {
      showToast(vinError)
      return
    }

    setLoading(true)
    try {
      const response = await decodeVin(vinField.value)
      const vinSections = (response?.data as { sections?: { section: string; fields: Record<string, { value: unknown; confidence: number; source: string }> }[] })?.sections ?? []

      const payload: FieldResponsePayload = {
        sections: Object.fromEntries(
          vinSections.map(({ section, fields }) => [
            section,
            {
              section,
              fields: Object.fromEntries(
                Object.entries(fields).map(([key, entry]) => [
                  key,
                  { value: String(entry.value ?? ''), confidence: entry.confidence ?? 1, source: entry.source },
                ])
              ),
            },
          ])
        ),
      }

      const { mappedFields: allVinMappedFields } = mapResponseToForm(payload, ALL_SECTIONS)
      const mappedFields = allVinMappedFields.filter(f => f.value && f.value.trim() !== '' && f.value.trim() !== 'No Data Found')
      if (mappedFields.length > 0) {
        setVinMappedFields(mappedFields)
        setVinModalOpen(true)
      }
    } catch (err) {
      log.error('VIN decode failed', err instanceof Error ? err : new Error(String(err)))
      const apiMsg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
      showToast(apiMsg ?? getMessage('vin.error.decode_failed'), 'error')
    } finally {
      setLoading(false)
    }
  }

  // Info Labels upload handler — enqueues as OCR item in the shared queue
  const handleInfoLabelChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    e.target.value = ''
    if (!checkPhotoLimit(1, 'popup', () => e.target.click())) return

    const ext = file.name.slice(file.name.lastIndexOf('.')).toLowerCase()
    if (!INFO_LABEL_ALLOWED_EXT.has(ext)) {
      setUnsupportedExt({ ext, source: 'info-label' })
      return
    }

    if (!checkFileSize(file.size)) return

    const currentCaseId = useValuationStore.getState().caseId ?? ''
    const currentUserId = String(useAuthStore.getState().user?.userId ?? '')
    if (!currentUserId || !currentCaseId) {
      showToast(getMessage('photo.error.missing_info_label_ref'), 'error')
      return
    }

    setOcrLoading(true)
    const hashResult = await checkContentHash(file)
    if (hashResult.status === 'duplicate') {
      showToast(getMessage('photo_strip.error_duplicate'), 'error')
      setOcrLoading(false)
      return
    }
    const contentHash = hashResult.hash

    const photoId = addPhoto({
      sectionId,
      blob: file,
      type: 'upload',
      name: file.name,
      mimeType: file.type,
      blobSection: 'ocr',
      contentHash,
    })

    queueRef.current.push({ file, photoId, kind: 'ocr' })
    processQueue()
  }

  const handleApplyAI = () => {
    applyAIFields(pendingMappedFields, true)
    setAiModalOpen(false)
    setPendingMappedFields([])
    setPendingPhotoIds([])
    setPendingWarningText(null)
  }

  const handleCancelAI = () => {
    // processAiImages always uploads to blob before AI inference, so force immediate
    // blob delete for every newly-uploaded photo in this popup (one for a single-image
    // upload, all of a batch's successfully-processed photos for a multi-image upload).
    // Re-detect/aggregate-view popups never populate pendingPhotoIds, so they're unaffected.
    for (const id of pendingPhotoIds) {
      removePhoto(id, { forceDelete: true })
    }
    setAiModalOpen(false)
    setPendingMappedFields([])
    setPendingPhotoIds([])
    setPendingWarningText(null)
  }

  // Opens the remove-image modal: loads this photo's detected fields from photoResults,
  // filters to only those whose form value still matches the AI/OCR-detected value
  // (i.e. not manually overridden by the user), and shows them as "will be deleted".
  // Fields excluded from the remove-image modal and from selective field cleanup.
  // vehicleType, use, and type drive section visibility and dropdown population,
  // so they must never be auto-cleared when a photo is removed.
  const REMOVE_EXCLUDED_FIELDS = new Set(['vehicleType', 'use', 'type'])

  const handleRemoveClick = (photo: typeof sectionPhotos[0]) => {
    const fields = photoResults[photo.id] ?? []
    const { formValues: fv, photoResults: allResults, photos: allPhotos } = useValuationStore.getState()
    // Use all photos across all sections — a backing image may live in a different section.
    const remainingPhotos = allPhotos.filter(p => p.id !== photo.id)

    // Fields where formValues still matches the detected value (user hasn't overridden)
    const matchedFields = fields.filter(
      (f) =>
        !REMOVE_EXCLUDED_FIELDS.has(f.fieldKey) &&
        fv[f.sectionId]?.[f.fieldKey] === f.value
    )

    // Fields backed by at least one remaining photo that detected the same fieldKey
    // (value need not be identical — if another image covers this field, we retain it silently).
    const backedByOther = matchedFields.filter(f =>
      remainingPhotos.some(p =>
        (allResults[p.id] ?? []).some(of => of.fieldKey === f.fieldKey)
      )
    )

    // Only unbacked fields go into the popup — backed fields are retained silently
    const fieldsToShow = matchedFields.filter(f => !backedByOther.includes(f))

    // For each backed field, find the highest confidence from the remaining images
    // so the field's confidence badge can be promoted after the photo is removed.
    const confidenceUpdates = backedByOther.map(f => {
      const best = remainingPhotos.reduce((max, p) => {
        const match = (allResults[p.id] ?? []).find(of => of.fieldKey === f.fieldKey)
        return match && (match.confidence ?? 0) > max ? (match.confidence ?? 0) : max
      }, 0)
      return { sectionId: f.sectionId, fieldKey: f.fieldKey, confidence: best }
    }).filter(u => u.confidence > 0)

    setRemoveModalFields(toModalFields(fieldsToShow))
    setRemoveModalMappedFields(fieldsToShow)
    setRemoveModalHasRetainedFields(backedByOther.length > 0)
    setRemoveModalConfidenceUpdates(confidenceUpdates)
    setRemoveModalPhotoId(photo.id)
    setRemoveModalOpen(true)
  }

  const handleRemovePhoto = (photoId: string, mappedFieldsToRemove: MappedField[]) => {
    const photo = sectionPhotos.find(p => p.id === photoId)
    const remaining = sectionPhotos.filter(p => p.id !== photoId)
    removePhoto(photoId)
    clearPhotoResult(photoId)

    // Clear only the specific form fields shown in the remove modal using fieldKey (not label).
    // vehicleType, use, and type are already excluded at the handleRemoveClick filter stage
    // so they will never appear in mappedFieldsToRemove.
    if (mappedFieldsToRemove.length > 0) {
      clearSpecificFields(
        mappedFieldsToRemove
          .filter((f) => f.sectionId && f.fieldKey)
          .map((f) => ({ sectionId: f.sectionId, fieldKey: f.fieldKey }))
      )
    }

    // Promote confidence badges for backed fields to the best score from remaining images.
    if (removeModalConfidenceUpdates.length > 0) {
      promoteFieldConfidence(removeModalConfidenceUpdates)
    }

    const remainingOcrPhotos = remaining.filter(p => p.blobSection === 'ocr' || p.blobSection === 'form-recognizer')
    const remainingAiPhotos = remaining.filter(p => p.blobSection !== 'ocr' && p.blobSection !== 'form-recognizer')

    // Use metadata-only variants so clearSpecificFields (above) remains the sole authority
    // on which form values get cleared — it already excludes backed fields correctly.
    if (photo?.blobSection === 'ocr' || photo?.blobSection === 'form-recognizer') {
      if (remainingOcrPhotos.length === 0) clearSectionOcrMetadata(sectionId)
    } else {
      if (remainingAiPhotos.length === 0) clearSectionAIMetadata(sectionId)
    }

    if (remaining.length === 0) {
      if (photo?.blobSection === 'ocr' || photo?.blobSection === 'form-recognizer') clearSectionAIMetadata(sectionId)
      else clearSectionOcrMetadata(sectionId)
    }
  }

  const modalFields = toModalFields(pendingMappedFields)

  return (
    <div className="py-[15px] border-b border-dashed border-[var(--border)] mb-[15px]">
      {/* Header */}
      <div
        className={cn(
          'flex items-center gap-[10px] mb-3',
          'font-ui text-[11px] font-bold uppercase text-[var(--text-dim)]',
          'after:content-[""] after:flex-1 after:h-px after:bg-[var(--border)]'
        )}
        style={{ fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif' }}
      >
        <Camera className="w-[13px] h-[13px]" /> {getMessage('photo_strip.section_header')}
      </div>

      {/* Action buttons */}
      <div
        className="flex gap-[10px] mb-3 flex-wrap items-center"
        data-tour={sectionId === 'standard-equipment' ? 'capture-upload-options' : undefined}
      >
        {/* Capture — dropdown for standard-equipment, additional-options, body-information; plain button elsewhere */}
        {sectionId === 'standard-equipment' ? (
          <div ref={captureMenuRef} className="relative">
            <button
              type="button"
              disabled={isSubmitted || loading || ocrLoading}
              onClick={() => !(loading || ocrLoading) && setCaptureMenuOpen((prev) => !prev)}
              className={cn(
                'flex items-center gap-[7px] px-4 py-2 rounded-[var(--radius-sm)]',
                'border border-[var(--border-active)] bg-[rgba(61,142,240,0.05)]',
                'text-[var(--accent)] text-[12px] font-medium cursor-pointer select-none',
                'hover:bg-[var(--accent)] hover:text-white hover:border-[var(--accent)]',
                'transition-all duration-[180ms]',
                captureMenuOpen && 'bg-[var(--accent)] text-white border-[var(--accent)]',
                (loading || ocrLoading) && 'opacity-50 cursor-not-allowed'
              )}
            >
              <Camera className="w-4 h-4" />
              <span>{getMessage('photo_strip.button_capture')}</span>
              <span className={cn('text-[10px] transition-transform duration-[180ms]', captureMenuOpen && 'rotate-180')}>▾</span>
            </button>
            {captureMenuOpen && (
              <div className={cn(
                'absolute left-0 top-[calc(100%+4px)] z-50 min-w-[148px]',
                'bg-[var(--surface)] border border-[var(--border)]',
                'rounded-[var(--radius-sm)] shadow-lg overflow-hidden',
                'animate-slide-down'
              )}>
                <button
                  type="button"
                  disabled={isSubmitted || ocrLoading}
                  onClick={() => { if (!ocrLoading && requireClaimRef() && checkPhotoLimit()) { setCaptureMenuOpen(false); setCaptureKind('ocr'); setCameraOpen(true) } }}
                  title={getMessage('photo_strip.tooltip_menu_capture_info_labels')}
                  className={cn(
                    'w-full flex items-center gap-[10px] px-4 py-[9px]',
                    'text-[12px] text-[var(--text)] font-medium text-left',
                    'hover:bg-[rgba(61,142,240,0.08)] hover:text-[var(--accent)]',
                    'transition-colors duration-[150ms] border-b border-[var(--border)]',
                    ocrLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                  )}
                >
                  <ScanText className="w-4 h-4" />
                  {getMessage('photo_strip.menu_info_labels')}
                </button>
                <button
                  type="button"
                  disabled={isSubmitted || loading}
                  onClick={() => { if (!loading && requireClaimRef() && checkPhotoLimit()) { setCaptureMenuOpen(false); setCaptureKind('ai'); setCameraOpen(true) } }}
                  title={getMessage('photo_strip.tooltip_menu_capture_image')}
                  className={cn(
                    'w-full flex items-center gap-[10px] px-4 py-[9px]',
                    'text-[12px] text-[var(--text)] font-medium text-left',
                    'hover:bg-[rgba(61,142,240,0.08)] hover:text-[var(--accent)]',
                    'transition-colors duration-[150ms]',
                    loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                  )}
                >
                  <ImageIcon className="w-4 h-4" />
                  {getMessage('photo_strip.menu_image')}
                </button>
              </div>
            )}
          </div>
        ) : sectionId === 'additional-options' ? (
          <div ref={additionalCaptureMenuRef} className="relative">
            <button
              type="button"
              disabled={isSubmitted || loading || ocrLoading}
              onClick={() => !(loading || ocrLoading) && setAdditionalCaptureMenuOpen((prev) => !prev)}
              className={cn(
                'flex items-center gap-[7px] px-4 py-2 rounded-[var(--radius-sm)]',
                'border border-[var(--border-active)] bg-[rgba(61,142,240,0.05)]',
                'text-[var(--accent)] text-[12px] font-medium cursor-pointer select-none',
                'hover:bg-[var(--accent)] hover:text-white hover:border-[var(--accent)]',
                'transition-all duration-[180ms]',
                additionalCaptureMenuOpen && 'bg-[var(--accent)] text-white border-[var(--accent)]',
                (loading || ocrLoading) && 'opacity-50 cursor-not-allowed'
              )}
            >
              <Camera className="w-4 h-4" />
              <span>{getMessage('photo_strip.button_capture')}</span>
              <span className={cn('text-[10px] transition-transform duration-[180ms]', additionalCaptureMenuOpen && 'rotate-180')}>▾</span>
            </button>
            {additionalCaptureMenuOpen && (
              <div className={cn(
                'absolute left-0 top-[calc(100%+4px)] z-50 min-w-[148px]',
                'bg-[var(--surface)] border border-[var(--border)]',
                'rounded-[var(--radius-sm)] shadow-lg overflow-hidden',
                'animate-slide-down'
              )}>
                <button
                  type="button"
                  disabled={isSubmitted || ocrLoading}
                  onClick={() => { if (!ocrLoading && requireClaimRef() && checkPhotoLimit()) { setAdditionalCaptureMenuOpen(false); setCaptureKind('ocr'); setCameraOpen(true) } }}
                  title={getMessage('photo_strip.tooltip_menu_capture_info_labels')}
                  className={cn(
                    'w-full flex items-center gap-[10px] px-4 py-[9px]',
                    'text-[12px] text-[var(--text)] font-medium text-left',
                    'hover:bg-[rgba(61,142,240,0.08)] hover:text-[var(--accent)]',
                    'transition-colors duration-[150ms] border-b border-[var(--border)]',
                    ocrLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                  )}
                >
                  <ScanText className="w-4 h-4" />
                  {getMessage('photo_strip.menu_info_labels')}
                </button>
                <button
                  type="button"
                  disabled={isSubmitted || loading}
                  onClick={() => { if (!loading && requireClaimRef() && checkPhotoLimit()) { setAdditionalCaptureMenuOpen(false); setCaptureKind('ai'); setCameraOpen(true) } }}
                  title={getMessage('photo_strip.tooltip_menu_capture_image')}
                  className={cn(
                    'w-full flex items-center gap-[10px] px-4 py-[9px]',
                    'text-[12px] text-[var(--text)] font-medium text-left',
                    'hover:bg-[rgba(61,142,240,0.08)] hover:text-[var(--accent)]',
                    'transition-colors duration-[150ms]',
                    loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                  )}
                >
                  <ImageIcon className="w-4 h-4" />
                  {getMessage('photo_strip.menu_image')}
                </button>
              </div>
            )}
          </div>
        ) : sectionId === 'body-information' && hasVisibleFields ? (
          <div ref={bodyCaptureMenuRef} className="relative">
            <button
              type="button"
              disabled={isSubmitted || loading || ocrLoading}
              onClick={() => !(loading || ocrLoading) && setBodyCaptureMenuOpen((prev) => !prev)}
              className={cn(
                'flex items-center gap-[7px] px-4 py-2 rounded-[var(--radius-sm)]',
                'border border-[var(--border-active)] bg-[rgba(61,142,240,0.05)]',
                'text-[var(--accent)] text-[12px] font-medium cursor-pointer select-none',
                'hover:bg-[var(--accent)] hover:text-white hover:border-[var(--accent)]',
                'transition-all duration-[180ms]',
                bodyCaptureMenuOpen && 'bg-[var(--accent)] text-white border-[var(--accent)]',
                (loading || ocrLoading) && 'opacity-50 cursor-not-allowed'
              )}
            >
              <Camera className="w-4 h-4" />
              <span>{getMessage('photo_strip.button_capture')}</span>
              <span className={cn('text-[10px] transition-transform duration-[180ms]', bodyCaptureMenuOpen && 'rotate-180')}>▾</span>
            </button>
            {bodyCaptureMenuOpen && (
              <div className={cn(
                'absolute left-0 top-[calc(100%+4px)] z-50 min-w-[148px]',
                'bg-[var(--surface)] border border-[var(--border)]',
                'rounded-[var(--radius-sm)] shadow-lg overflow-hidden',
                'animate-slide-down'
              )}>
                <button
                  type="button"
                  disabled={isSubmitted || ocrLoading}
                  onClick={() => { if (!ocrLoading && requireClaimRef() && checkPhotoLimit()) { setBodyCaptureMenuOpen(false); setCaptureKind('ocr'); setCameraOpen(true) } }}
                  title={getMessage('photo_strip.tooltip_menu_capture_info_labels')}
                  className={cn(
                    'w-full flex items-center gap-[10px] px-4 py-[9px]',
                    'text-[12px] text-[var(--text)] font-medium text-left',
                    'hover:bg-[rgba(61,142,240,0.08)] hover:text-[var(--accent)]',
                    'transition-colors duration-[150ms] border-b border-[var(--border)]',
                    ocrLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                  )}
                >
                  <ScanText className="w-4 h-4" />
                  {getMessage('photo_strip.menu_info_labels')}
                </button>
                <button
                  type="button"
                  disabled={isSubmitted || loading}
                  onClick={() => { if (!loading && requireClaimRef() && checkPhotoLimit()) { setBodyCaptureMenuOpen(false); setCaptureKind('ai'); setCameraOpen(true) } }}
                  title={getMessage('photo_strip.tooltip_menu_capture_image')}
                  className={cn(
                    'w-full flex items-center gap-[10px] px-4 py-[9px]',
                    'text-[12px] text-[var(--text)] font-medium text-left',
                    'hover:bg-[rgba(61,142,240,0.08)] hover:text-[var(--accent)]',
                    'transition-colors duration-[150ms]',
                    loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                  )}
                >
                  <ImageIcon className="w-4 h-4" />
                  {getMessage('photo_strip.menu_image')}
                </button>
              </div>
            )}
          </div>
        ) : (
          <button
            type="button"
            disabled={isSubmitted || loading || ocrLoading}
            onClick={() => { if (requireClaimRef() && checkPhotoLimit()) { setCaptureKind('ai'); setCameraOpen(true) } }}
            className={cn(
              'flex items-center gap-[7px] px-4 py-2 rounded-[var(--radius-sm)]',
              'border border-[var(--border-active)] bg-[rgba(61,142,240,0.05)]',
              'text-[var(--accent)] text-[12px] font-medium cursor-pointer',
              'hover:bg-[var(--accent)] hover:text-white hover:border-[var(--accent)]',
              'transition-all duration-[180ms]',
              (loading || ocrLoading) && 'opacity-50 cursor-not-allowed'
            )}
          >
            <Camera className="w-4 h-4" /> {getMessage('photo_strip.button_capture')}
          </button>
        )}

        <CameraModal
          open={cameraOpen}
          onCapture={handleCaptured}
          onClose={() => setCameraOpen(false)}
        />

        {/* Upload — dropdown for standard-equipment and additional-options, plain button elsewhere */}
        {sectionId === 'standard-equipment' ? (
          <div ref={uploadMenuRef} className="relative">
            {/* Split-style Upload button */}
            <button
              type="button"
              disabled={isSubmitted || loading || ocrLoading}
              onClick={() => !(loading || ocrLoading) && setUploadMenuOpen((prev) => !prev)}
              className={cn(
                'flex items-center gap-[7px] px-4 py-2 rounded-[var(--radius-sm)]',
                'border border-[var(--border-active)] bg-[rgba(61,142,240,0.05)]',
                'text-[var(--accent)] text-[12px] font-medium cursor-pointer select-none',
                'hover:bg-[var(--accent)] hover:text-white hover:border-[var(--accent)]',
                'transition-all duration-[180ms]',
                uploadMenuOpen && 'bg-[var(--accent)] text-white border-[var(--accent)]',
                (loading || ocrLoading) && 'opacity-50 cursor-not-allowed'
              )}
            >
              <span>↑</span>
              <span>{getMessage('photo_strip.button_upload')}</span>
              <span
                className={cn(
                  'text-[10px] transition-transform duration-[180ms]',
                  uploadMenuOpen && 'rotate-180'
                )}
              >
                ▾
              </span>
            </button>

            {/* Dropdown menu */}
            {uploadMenuOpen && (
              <div
                className={cn(
                  'absolute left-0 top-[calc(100%+4px)] z-50 min-w-[148px]',
                  'bg-[var(--surface)] border border-[var(--border)]',
                  'rounded-[var(--radius-sm)] shadow-lg overflow-hidden',
                  'animate-slide-down'
                )}
              >
                {/* Info Labels */}
                <button
                  type="button"
                  disabled={isSubmitted || ocrLoading}
                  onClick={() => { if (!ocrLoading && requireClaimRef() && checkPhotoLimit()) { setUploadMenuOpen(false); infoLabelInputRef.current?.click() } }}
                  title={getMessage('photo_strip.tooltip_menu_info_labels')}
                  className={cn(
                    'w-full flex items-center gap-[10px] px-4 py-[9px]',
                    'text-[12px] text-[var(--text)] font-medium text-left',
                    'hover:bg-[rgba(61,142,240,0.08)] hover:text-[var(--accent)]',
                    'transition-colors duration-[150ms] border-b border-[var(--border)]',
                    ocrLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                  )}
                >
                  <ScanText className="w-4 h-4" />
                  {getMessage('photo_strip.menu_info_labels')}
                </button>

                {/* Image */}
                <button
                  type="button"
                  disabled={isSubmitted || loading}
                  onClick={() => { if (!loading && requireClaimRef() && checkPhotoLimit()) { setUploadMenuOpen(false); imageInputRef.current?.click() } }}
                  title={getMessage('photo_strip.tooltip_menu_image')}
                  className={cn(
                    'w-full flex items-center gap-[10px] px-4 py-[9px]',
                    'text-[12px] text-[var(--text)] font-medium text-left',
                    'hover:bg-[rgba(61,142,240,0.08)] hover:text-[var(--accent)]',
                    'transition-colors duration-[150ms]',
                    loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                  )}
                >
                  <ImageIcon className="w-4 h-4" />
                  {getMessage('photo_strip.menu_image')}
                </button>
              </div>
            )}
          </div>
        ) : sectionId === 'additional-options' ? (
          <div ref={additionalUploadMenuRef} className="relative">
            <button
              type="button"
              disabled={isSubmitted || loading || ocrLoading}
              onClick={() => !(loading || ocrLoading) && setAdditionalUploadMenuOpen((prev) => !prev)}
              className={cn(
                'flex items-center gap-[7px] px-4 py-2 rounded-[var(--radius-sm)]',
                'border border-[var(--border-active)] bg-[rgba(61,142,240,0.05)]',
                'text-[var(--accent)] text-[12px] font-medium cursor-pointer select-none',
                'hover:bg-[var(--accent)] hover:text-white hover:border-[var(--accent)]',
                'transition-all duration-[180ms]',
                additionalUploadMenuOpen && 'bg-[var(--accent)] text-white border-[var(--accent)]',
                (loading || ocrLoading) && 'opacity-50 cursor-not-allowed'
              )}
            >
              <span>↑</span>
              <span>{getMessage('photo_strip.button_upload')}</span>
              <span
                className={cn(
                  'text-[10px] transition-transform duration-[180ms]',
                  additionalUploadMenuOpen && 'rotate-180'
                )}
              >
                ▾
              </span>
            </button>

            {additionalUploadMenuOpen && (
              <div
                className={cn(
                  'absolute left-0 top-[calc(100%+4px)] z-50 min-w-[148px]',
                  'bg-[var(--surface)] border border-[var(--border)]',
                  'rounded-[var(--radius-sm)] shadow-lg overflow-hidden',
                  'animate-slide-down'
                )}
              >
                {/* Info Labels */}
                <button
                  type="button"
                  disabled={isSubmitted || ocrLoading}
                  onClick={() => { if (!ocrLoading && requireClaimRef() && checkPhotoLimit()) { setAdditionalUploadMenuOpen(false); additionalInfoLabelInputRef.current?.click() } }}
                  title={getMessage('photo_strip.tooltip_menu_info_labels')}
                  className={cn(
                    'w-full flex items-center gap-[10px] px-4 py-[9px]',
                    'text-[12px] text-[var(--text)] font-medium text-left',
                    'hover:bg-[rgba(61,142,240,0.08)] hover:text-[var(--accent)]',
                    'transition-colors duration-[150ms] border-b border-[var(--border)]',
                    ocrLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                  )}
                >
                  <ScanText className="w-4 h-4" />
                  {getMessage('photo_strip.menu_info_labels')}
                </button>

                {/* Image */}
                <button
                  type="button"
                  disabled={isSubmitted || loading}
                  onClick={() => { if (!loading && requireClaimRef() && checkPhotoLimit()) { setAdditionalUploadMenuOpen(false); imageInputRef.current?.click() } }}
                  title={getMessage('photo_strip.tooltip_menu_image')}
                  className={cn(
                    'w-full flex items-center gap-[10px] px-4 py-[9px]',
                    'text-[12px] text-[var(--text)] font-medium text-left',
                    'hover:bg-[rgba(61,142,240,0.08)] hover:text-[var(--accent)]',
                    'transition-colors duration-[150ms]',
                    loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                  )}
                >
                  <ImageIcon className="w-4 h-4" />
                  {getMessage('photo_strip.menu_image')}
                </button>
              </div>
            )}
          </div>
        ) : sectionId === 'body-information' && hasVisibleFields ? (
          <div ref={bodyUploadMenuRef} className="relative">
            <button
              type="button"
              disabled={isSubmitted || loading || ocrLoading}
              onClick={() => !(loading || ocrLoading) && setBodyUploadMenuOpen((prev) => !prev)}
              className={cn(
                'flex items-center gap-[7px] px-4 py-2 rounded-[var(--radius-sm)]',
                'border border-[var(--border-active)] bg-[rgba(61,142,240,0.05)]',
                'text-[var(--accent)] text-[12px] font-medium cursor-pointer select-none',
                'hover:bg-[var(--accent)] hover:text-white hover:border-[var(--accent)]',
                'transition-all duration-[180ms]',
                bodyUploadMenuOpen && 'bg-[var(--accent)] text-white border-[var(--accent)]',
                (loading || ocrLoading) && 'opacity-50 cursor-not-allowed'
              )}
            >
              <span>↑</span>
              <span>{getMessage('photo_strip.button_upload')}</span>
              <span
                className={cn(
                  'text-[10px] transition-transform duration-[180ms]',
                  bodyUploadMenuOpen && 'rotate-180'
                )}
              >
                ▾
              </span>
            </button>

            {bodyUploadMenuOpen && (
              <div
                className={cn(
                  'absolute left-0 top-[calc(100%+4px)] z-50 min-w-[148px]',
                  'bg-[var(--surface)] border border-[var(--border)]',
                  'rounded-[var(--radius-sm)] shadow-lg overflow-hidden',
                  'animate-slide-down'
                )}
              >
                {/* Info Labels */}
                <button
                  type="button"
                  disabled={isSubmitted || ocrLoading}
                  onClick={() => { if (!ocrLoading && requireClaimRef() && checkPhotoLimit()) { setBodyUploadMenuOpen(false); bodyInfoLabelInputRef.current?.click() } }}
                  title={getMessage('photo_strip.tooltip_menu_info_labels')}
                  className={cn(
                    'w-full flex items-center gap-[10px] px-4 py-[9px]',
                    'text-[12px] text-[var(--text)] font-medium text-left',
                    'hover:bg-[rgba(61,142,240,0.08)] hover:text-[var(--accent)]',
                    'transition-colors duration-[150ms] border-b border-[var(--border)]',
                    ocrLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                  )}
                >
                  <ScanText className="w-4 h-4" />
                  {getMessage('photo_strip.menu_info_labels')}
                </button>

                {/* Image */}
                <button
                  type="button"
                  disabled={isSubmitted || loading}
                  onClick={() => { if (!loading && requireClaimRef() && checkPhotoLimit()) { setBodyUploadMenuOpen(false); imageInputRef.current?.click() } }}
                  title={getMessage('photo_strip.tooltip_menu_image')}
                  className={cn(
                    'w-full flex items-center gap-[10px] px-4 py-[9px]',
                    'text-[12px] text-[var(--text)] font-medium text-left',
                    'hover:bg-[rgba(61,142,240,0.08)] hover:text-[var(--accent)]',
                    'transition-colors duration-[150ms]',
                    loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                  )}
                >
                  <ImageIcon className="w-4 h-4" />
                  {getMessage('photo_strip.menu_image')}
                </button>
              </div>
            )}
          </div>
        ) : (
          <button
            type="button"
            disabled={isSubmitted || loading}
            onClick={handleUpload}
            className={cn(
              'flex items-center gap-[7px] px-4 py-2 rounded-[var(--radius-sm)]',
              'border border-[var(--border-active)] bg-[rgba(61,142,240,0.05)]',
              'text-[var(--accent)] text-[12px] font-medium cursor-pointer',
              'hover:bg-[var(--accent)] hover:text-white hover:border-[var(--accent)]',
              'transition-all duration-[180ms]',
              loading && 'opacity-50 cursor-not-allowed'
            )}
          >
            {getMessage('photo_strip.button_upload_file')}
          </button>
        )}

        {/* Loading Indicator */}
        {/* {loading && (
          <div className="flex items-center gap-2 text-[11px] text-blue-500 font-mono">
            <span className="animate-spin">⏳</span>
            {getMessage('photo_strip.processing_image')}
          </div>
        )} */}

      </div>

      {/* Upload limit indicator */}
      {(() => {
        const limit = getPhotoLimit(sectionId)
        const current = sectionPhotos.length
        const isAtLimit = current >= limit
        return (
          <div className="flex items-center gap-[6px] mt-[6px] mb-[10px] text-[11px]">
            <span className="inline-flex items-center gap-[4px] px-[7px] py-[2px] rounded-full font-medium bg-[var(--surface2)] border border-[var(--border)] text-[var(--text-dim)]">
              {isAtLimit
                ? getMessage('photo_strip.upload_limit_reached', { current: String(current), limit: String(limit) })
                : getMessage('photo_strip.upload_count', { current: String(current), limit: String(limit) })}
            </span>
          </div>
        )
      })()}

      {/* Hidden inputs */}
      <input ref={fileInputRef} type="file" accept={IMAGE_ACCEPT} multiple className="hidden" onChange={handleFileChange} />
      {/* Info Labels — OCR path */}
      <input ref={infoLabelInputRef} type="file" accept={IMAGE_ACCEPT} className="hidden" onChange={handleInfoLabelChange} />
      {/* Image — AI inference path */}
      <input ref={imageInputRef} type="file" accept={IMAGE_ACCEPT} multiple className="hidden" onChange={handleFileChange} />
      {/* Additional Options — Info Labels OCR path */}
      <input ref={additionalInfoLabelInputRef} type="file" accept={IMAGE_ACCEPT} className="hidden" onChange={handleInfoLabelChange} />
      {/* Body Information — Info Labels OCR path */}
      <input ref={bodyInfoLabelInputRef} type="file" accept={IMAGE_ACCEPT} className="hidden" onChange={handleInfoLabelChange} />

      {/* Thumbnail grid + View AI Data button */}
      {sectionPhotos.length > 0 && (
        <div className="flex flex-wrap gap-[10px] items-end">
          {sectionPhotos.map((photo, idx) => (
            <div
              key={photo.id}
              className="relative w-[88px] h-[68px] rounded-[8px] overflow-hidden border border-[var(--border)] flex-shrink-0 group"
            >
              {/* Clickable area opens lightbox */}
              <button
                type="button"
                onClick={() => setLightboxIndex(idx)}
                className="w-full h-full block cursor-zoom-in"
                title={getMessage('photo_strip.tooltip_click_view')}
              >
                {photo.mimeType === 'application/pdf' ? (
                  <div className="w-full h-full flex flex-col items-center justify-center gap-[3px] bg-[var(--surface2)]">
                    <span className="text-[22px]">📎</span>
                    <span className="text-[8px] text-[var(--text-muted)] px-1 text-center leading-tight">
                      {photo.name ? photo.name.substring(0, 12) : 'PDF'}
                    </span>
                  </div>
                ) : (
                  <img
                    src={photo.blobUrl}
                    alt={photo.name ?? 'photo'}
                    className="w-full h-full object-cover transition-transform duration-150 group-hover:scale-105"
                  />
                )}
              </button>

              {/* Expand hint overlay */}
              <div className={cn(
                'absolute inset-0 flex items-center justify-center',
                'bg-black/0 group-hover:bg-black/30 transition-all duration-150 pointer-events-none'
              )}>
                <span className="opacity-0 group-hover:opacity-100 text-white text-[16px] transition-opacity duration-150">
                  🔍
                </span>
              </div>

              {/* Remove */}
              {!isSubmitted && (
                <button
                  type="button"
                  onClick={() => handleRemoveClick(photo)}
                  className={cn(
                    'absolute top-[3px] right-[3px] z-10',
                    'w-[17px] h-[17px] rounded-full',
                    'bg-[rgba(220,38,38,0.82)] text-white text-[9px]',
                    'flex items-center justify-center cursor-pointer',
                    'hover:bg-[var(--error)] transition-[background] duration-150'
                  )}
                >
                  ✕
                </button>
              )}
  
              {/* Per-image frosted bar — AI re-detects from blob; OCR re-scans from blob */}
              {photo.blobSection === 'ai' && photo.serverName && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleRedetect(photo) }}
                  className={cn(
                    'absolute bottom-0 left-0 right-0 z-10 h-[24px] sm:h-[17px]',
                    'flex items-center justify-between px-[6px]',
                    'bg-black/30 backdrop-blur-[2px]',
                    'hover:bg-black/50 transition-colors duration-150 cursor-pointer'
                  )}
                  title={getMessage('photo_strip.tooltip_view_ai_single')}
                >
                  <div className="flex items-center gap-[3px]">
                    <Sparkles size={8} strokeWidth={2.5} className="text-green-300" />
                    <span className="text-[7px] font-bold truncate text-green-200">
                      {getMessage('photo_strip.label_ai_results')}
                    </span>
                  </div>
                  <span className="text-[7px] text-white/80">View</span>
                </button>
              )}
              {photo.blobSection === 'ocr' && photo.serverName && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleRescan(photo) }}
                  className={cn(
                    'absolute bottom-0 left-0 right-0 z-10 h-[24px] sm:h-[17px]',
                    'flex items-center justify-between px-[6px]',
                    'bg-black/30 backdrop-blur-[2px]',
                    'hover:bg-black/50 transition-colors duration-150 cursor-pointer'
                  )}
                  title={getMessage('photo_strip.tooltip_view_ocr')}
                >
                  <div className="flex items-center gap-[3px]">
                    <ScanText size={8} strokeWidth={2.5} className="text-blue-300" />
                    <span className="text-[7px] font-bold truncate text-blue-200">
                      {getMessage('photo_strip.label_scan_results')}
                    </span>
                  </div>
                  <span className="text-[7px] text-white/80">View</span>
                </button>
              )}
            </div>
          ))}

          {/* Aggregate AI tile */}
          {sectionPhotos.some(p => p.blobSection === 'ai' && p.serverName) && (
            <button
              type="button"
              onClick={handleViewAggAi}
              className={cn(
                'relative w-[88px] h-[68px] flex-shrink-0 rounded-[8px]',
                'flex flex-col items-center justify-center gap-[4px]',
                'border border-dashed border-green-400 bg-green-50',
                'text-green-700 cursor-pointer',
                'hover:bg-green-100 hover:border-green-500 transition-all duration-[180ms]'
              )}
              title={getMessage('photo_strip.tooltip_view_ai')}
            >
              <Sparkles size={20} strokeWidth={1.5} className="text-green-600" />
              <span className="text-[11px] sm:text-[9px] font-bold leading-tight text-center px-1">
                {getMessage('photo_strip.button_view_ai')}
              </span>
            </button>
          )}

          {/* Aggregate OCR tile — visible whenever there are uploaded info label photos */}
          {sectionPhotos.some((p) => p.blobSection === 'ocr' && p.serverName) && (
            <button
              type="button"
              onClick={handleViewInfoLabel}
              className={cn(
                'relative w-[88px] h-[68px] flex-shrink-0 rounded-[8px]',
                'flex flex-col items-center justify-center gap-[4px]',
                'border border-dashed border-blue-400 bg-blue-50',
                'text-blue-700 cursor-pointer',
                'hover:bg-blue-100 hover:border-blue-500 transition-all duration-[180ms]'
              )}
              title={getMessage('photo_strip.tooltip_view_ocr')}
            >
              <ScanText size={20} strokeWidth={1.5} className="text-blue-600" />
              <span className="text-[11px] sm:text-[9px] font-bold leading-tight text-center px-1">
                {getMessage('photo_strip.button_view_ocr')}
              </span>
            </button>
          )}
        </div>
      )}

      <AiProcessModal
        open={removeModalOpen}
        title={getMessage(removeModalFields.length > 0 ? 'photo.confirm.remove_title' : 'photo.confirm.remove_image_label')}
        applyLabel={getMessage(removeModalFields.length > 0 ? 'photo.confirm.remove_title' : 'photo.confirm.remove_image_label')}
        fields={removeModalFields}
        warningKey={removeModalFields.length > 0 ? 'photo.confirm.remove_warning' : undefined}
        infoKey={removeModalHasRetainedFields ? 'photo.confirm.remove_retained_fields' : undefined}
        emptyMessageKey="photo.confirm.remove_no_fields_message"
        showApplyWhenEmpty
        hideConfidence
        onContinue={() => {
          handleRemovePhoto(removeModalPhotoId!, removeModalMappedFields)
          setRemoveModalOpen(false)
          setRemoveModalPhotoId(null)
          setRemoveModalFields([])
          setRemoveModalMappedFields([])
          setRemoveModalHasRetainedFields(false)
          setRemoveModalConfidenceUpdates([])
        }}
        onCancel={() => {
          setRemoveModalOpen(false)
          setRemoveModalPhotoId(null)
          setRemoveModalFields([])
          setRemoveModalMappedFields([])
          setRemoveModalHasRetainedFields(false)
          setRemoveModalConfidenceUpdates([])
        }}
      />

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <PhotoLightbox
          photos={sectionPhotos}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}

      <AiProcessModal
        open={aiModalOpen}
        fields={modalFields}
        onContinue={handleApplyAI}
        onCancel={handleCancelAI}
        readOnly={isSubmitted}
        warningText={pendingWarningText ?? undefined}
      />

      <ConfirmDialog
        open={!!bodyMismatchInfo}
        title={getMessage('photo.confirm.body_type_mismatch_title')}
        message={bodyMismatchInfo
          ? getMessage('photo.confirm.body_type_mismatch_warning', bodyMismatchInfo)
          : ''}
        confirmLabel={getMessage('common.button.ok')}
        hideCancel
        onConfirm={() => setBodyMismatchInfo(null)}
        onCancel={() => setBodyMismatchInfo(null)}
      />

      <ConfirmDialog
        open={!!vtMismatchInfo}
        title={getMessage('photo.confirm.vehicle_type_mismatch_title')}
        message={getMessage('photo.confirm.vehicle_type_mismatch_warning', vtMismatchInfo ?? {})}
        confirmLabel={getMessage('common.button.ok')}
        hideCancel
        onConfirm={() => setVtMismatchInfo(null)}
        onCancel={() => setVtMismatchInfo(null)}
      />

      {/* OCR Results modal */}
      <AiProcessModal
        open={ocrModalOpen}
        title={getMessage('ocr.modal.ocr_results_title')}
        applyLabel={getMessage('ai_modal.button_apply')}
        fields={ocrFields}
        readOnly={isSubmitted}
        onCancel={() => {
          if (pendingOcrPhotoIdRef.current) {
            // processOcrImage always uploads to blob — force immediate delete even with a saved draft.
            removePhoto(pendingOcrPhotoIdRef.current, { forceDelete: true })
            pendingOcrPhotoIdRef.current = undefined
          }
          setOcrModalOpen(false)
          setOcrMappedFields([])
        }}
        onContinue={() => {
          if (ocrMappedFields.length === 0 && pendingOcrPhotoIdRef.current) {
            removePhoto(pendingOcrPhotoIdRef.current)
          }
          pendingOcrPhotoIdRef.current = undefined
          const vinFieldBeforeApply = ocrMappedFields.find(f => f.fieldKey === 'vin' && f.value)
          const previousVinValue = vinFieldBeforeApply
            ? (useValuationStore.getState().formValues[vinFieldBeforeApply.sectionId] as Record<string, string> | undefined)?.['vin']
            : undefined
          applyAIFields(ocrMappedFields, true)
          setOcrModalOpen(false)
          const snapshot = ocrMappedFields
          setOcrMappedFields([])
          const vinField = snapshot.find(f => f.fieldKey === 'vin' && f.value)
          const vinValueChanged = vinField && vinField.value.trim().toUpperCase() !== (previousVinValue ?? '').trim().toUpperCase()
          if (vinField && vinValueChanged && !FIELD_VALIDATORS.vin(vinField.value)) {
            setVinDecodeSnapshot(snapshot)
            setVinDecodeConfirmOpen(true)
          }
        }}
      />

      <ConfirmDialog
        open={vinDecodeConfirmOpen}
        title={getMessage('vin.confirm.title')}
        message={getMessage('vin.confirm.body')}
        confirmLabel={getMessage('vin.confirm.confirm')}
        cancelLabel={getMessage('vin.confirm.cancel')}
        onConfirm={() => {
          setVinDecodeConfirmOpen(false)
          setVinDecoding(true)
          handleVinDecode(vinDecodeSnapshot).finally(() => setVinDecoding(false))
          setVinDecodeSnapshot([])
        }}
        onCancel={() => {
          setVinDecodeConfirmOpen(false)
          setVinDecodeSnapshot([])
        }}
      />

      {/* VIN Decode results modal */}
      <AiProcessModal
        open={vinModalOpen}
        title={getMessage('ocr.modal.vin_results_title')}
        applyLabel={getMessage('ai_modal.button_apply')}
        fields={toModalFields(vinMappedFields)}
        hideConfidence
        readOnly={isSubmitted}
        onContinue={() => {
          setOcrResult(sectionId, vinMappedFields)
          applyAIFields(vinMappedFields, true)
          setVinModalOpen(false)
          setVinMappedFields([])
        }}
        onCancel={() => {
          setVinModalOpen(false)
          setVinMappedFields([])
        }}
      />

      {/* Unsupported file format warning */}
      <ConfirmDialog
        open={unsupportedExt !== null}
        title={getMessage('photo.confirm.unsupported_title')}
        message={getMessage(
          unsupportedExt?.source === 'image'
            ? 'photo.confirm.unsupported_image_body'
            : 'photo.confirm.unsupported_info_labels_body',
          { ext: unsupportedExt?.ext ?? '' }
        )}
        confirmLabel={getMessage('common.button.ok')}
        hideCancel
        onConfirm={() => setUnsupportedExt(null)}
        onCancel={() => setUnsupportedExt(null)}
      />

      {/* Per-image response modal */}
      <AiProcessModal
        open={viewImageDataPhotoId !== null}
        title={(() => {
          const p = sectionPhotos.find(ph => ph.id === viewImageDataPhotoId)
          return p?.blobSection === 'ocr' ? getMessage('ocr.modal.ocr_results_title') : getMessage('ocr.modal.ai_results_title')
        })()}
        applyLabel={getMessage('ai_modal.button_apply')}
        fields={toModalFields(photoResults[viewImageDataPhotoId ?? ''] ?? [])}
        readOnly={isSubmitted}
        onContinue={() => {
          applyAIFields(photoResults[viewImageDataPhotoId ?? ''] ?? [], true)
          setViewImageDataPhotoId(null)
        }}
        onCancel={() => setViewImageDataPhotoId(null)}
      />


      {/* Full-page overlay while OCR is processing */}
      {ocrLoading && (
        <div className="fixed inset-0 z-[800] bg-black/40 backdrop-blur-[2px] flex items-center justify-center px-4">
          <div className={cn(
            'flex items-center gap-3 px-4 sm:px-6 py-3 sm:py-4 shadow-2xl w-full max-w-[320px]',
            'bg-[var(--surface)] border border-[var(--border)] rounded-[var(--radius)]'
          )}>
            <Loader2 size={22} className="text-[var(--accent)] animate-spin flex-shrink-0" />
            <div>
              <p className="text-[13px] font-bold text-[var(--text)]">{getMessage('photo_strip.processing_info_labels_title')}</p>
              <p className="text-[11px] text-[var(--text-muted)]">{getMessage('photo_strip.processing_info_labels_subtitle')}</p>
            </div>
          </div>
        </div>
      )}

      {/* Full-page overlay while AI is processing or VIN is decoding — disables the entire page */}
      {(loading || vinDecoding) && (
        <div className="fixed inset-0 z-[800] bg-black/40 backdrop-blur-[2px] flex items-center justify-center px-4">
          <div className={cn(
            'flex items-center gap-3 px-4 sm:px-6 py-3 sm:py-4 shadow-2xl w-full max-w-[320px]',
            'bg-[var(--surface)] border border-[var(--border)] rounded-[var(--radius)]'
          )}>
            <Loader2 size={22} className="text-[var(--accent)] animate-spin flex-shrink-0" />
            <div>
              <p className="text-[13px] font-bold text-[var(--text)]">
                {vinDecoding ? getMessage('vin.info.loading') : (
                  aiProcessingTotal > 1
                    ? getMessage('photo_strip.processing_image_progress_title', { current: String(aiProcessingCurrent), total: String(aiProcessingTotal) })
                    : getMessage('photo_strip.processing_image_title')
                )}
              </p>
              <p className="text-[11px] text-[var(--text-muted)]">{vinDecoding ? getMessage('vin.info.loading_subtitle') : getMessage('photo_strip.processing_image_subtitle')}</p>
            </div>
          </div>
        </div>
      )}

      {/* Limit exceeded popup — shows when user selects more files than remaining slots */}
      <ConfirmDialog
        open={limitExceededOpen}
        title="Upload Limit Reached"
        message={getMessage('photo_strip.limit_exceeded_dialog', {
          remaining: String(limitExceededCounts.remaining),
          selected: String(limitExceededCounts.selected)
        })}
        confirmLabel="Select Fewer Files"
        cancelLabel="Ok"
        onConfirm={() => {
          setLimitExceededOpen(false)
          limitExceededCounts.retryCallback?.()
        }}
        onCancel={() => setLimitExceededOpen(false)}
      />
    </div>
  )
}
